/**
 * Gameplay viewport — stable backing store across browser zoom (Ctrl +/-).
 *
 * Browser zoom changes visualViewport size but not window.outerWidth.
 * Pin canvas backing pixels until the window chrome actually resizes.
 * Map pointer deltas via getBoundingClientRect() so CSS scaling stays exact.
 *
 * Touch / iPad: backing store tracks visualViewport CSS size × devicePixelRatio so
 * circles stay round and sharp; CSS box is set explicitly (no inset stretch).
 */

import { isPwaStandalone } from "../pwa-detect";

export function isMobileCoarseTouchViewport(): boolean {
    if (document.documentElement.classList.contains("touch-ipad")) return true;
    if (typeof window.matchMedia !== "function") return false;
    return (
        window.matchMedia("(pointer: coarse)").matches && window.matchMedia("(hover: none)").matches
    );
}

export function layoutViewportHeightPx(): number {
    return Math.round(
        window.innerHeight ||
            document.documentElement.clientHeight ||
            window.visualViewport?.height ||
            0,
    );
}

function visualViewportCssSize(): { w: number; h: number } {
    const vv = window.visualViewport;
    const cssW = vv?.width ?? window.innerWidth;
    const cssH = vv?.height ?? window.innerHeight;
    return { w: Math.max(1, Math.round(cssW)), h: Math.max(1, Math.round(cssH)) };
}

function gameplayDevicePixelRatio(): number {
    return Math.min(Math.max(window.devicePixelRatio || 1, 1), 3);
}

/** Backing-store pixels from CSS viewport size (keeps circles round on high-DPI touch screens). */
function backingSizeFromCss(css: { w: number; h: number }): { w: number; h: number } {
    const dpr = gameplayDevicePixelRatio();
    return {
        w: Math.max(1, Math.round(css.w * dpr)),
        h: Math.max(1, Math.round(css.h * dpr)),
    };
}

function computeLayoutBackingSize(): { w: number; h: number } {
    if (isMobileCoarseTouchViewport()) {
        return backingSizeFromCss(visualViewportCssSize());
    }
    const vv = window.visualViewport;
    if (
        vv &&
        Number.isFinite(vv.width) &&
        vv.width > 0 &&
        Number.isFinite(vv.height) &&
        vv.height > 0 &&
        Number.isFinite(vv.scale) &&
        vv.scale > 0
    ) {
        return {
            w: Math.max(1, Math.round(vv.width * vv.scale)),
            h: Math.max(1, Math.round(vv.height * vv.scale)),
        };
    }
    return {
        w: Math.max(1, Math.round(window.innerWidth)),
        h: Math.max(1, Math.round(layoutViewportHeightPx())),
    };
}

let pinnedBackingW = 0;
let pinnedBackingH = 0;
let lastOuterW = 0;
let lastOuterH = 0;

/** Update pinned backing size when the window resizes — not on browser zoom alone. */
export function refreshGameplayBackingStore(): boolean {
    const ow = window.outerWidth;
    const oh = window.outerHeight;

    if (isMobileCoarseTouchViewport()) {
        const size = computeLayoutBackingSize();
        const changed =
            pinnedBackingW <= 0 ||
            size.w !== pinnedBackingW ||
            size.h !== pinnedBackingH;
        if (changed) {
            pinnedBackingW = size.w;
            pinnedBackingH = size.h;
            lastOuterW = ow;
            lastOuterH = oh;
        }
        return changed;
    }

    const outerChanged = pinnedBackingW <= 0 || ow !== lastOuterW || oh !== lastOuterH;
    if (!outerChanged) return false;

    const size = computeLayoutBackingSize();
    pinnedBackingW = size.w;
    pinnedBackingH = size.h;
    lastOuterW = ow;
    lastOuterH = oh;
    return true;
}

/** CSS size of the visible gameplay surface (tracks visual viewport while zooming). */
export function gameplayViewportCssSize(): { w: number; h: number } {
    return visualViewportCssSize();
}

/** Internal canvas pixels — pinned until window.outerWidth/outerHeight changes. */
export function gameplayCanvasInternalSize(): { w: number; h: number } {
    refreshGameplayBackingStore();
    if (isMobileCoarseTouchViewport()) {
        return backingSizeFromCss(gameplayViewportCssSize());
    }
    return { w: pinnedBackingW, h: pinnedBackingH };
}

const GAMEPLAY_SURFACE_IDS = ["game", "gameCursorTop", "visualOverlay", "fxFlash", "touchPlaySteerPad"] as const;

function applyTouchSteerPadCss(el: HTMLElement) {
    if (isPwaStandalone()) {
        // iOS installed PWA: visualViewport offsets can shrink the pad and leave dead touch zones.
        el.style.inset = "0";
        el.style.left = "0";
        el.style.top = "0";
        el.style.right = "0";
        el.style.bottom = "0";
        el.style.width = "100%";
        el.style.height = "100%";
        el.style.maxWidth = "none";
        el.style.maxHeight = "none";
        el.style.minWidth = "0";
        el.style.minHeight = "0";
        return;
    }
    const css = gameplayViewportCssSize();
    const vv = window.visualViewport;
    const offsetLeft = vv?.offsetLeft ?? 0;
    const offsetTop = vv?.offsetTop ?? 0;
    el.style.inset = "unset";
    el.style.left = `${offsetLeft}px`;
    el.style.top = `${offsetTop}px`;
    el.style.width = `${css.w}px`;
    el.style.height = `${css.h}px`;
    el.style.maxWidth = `${css.w}px`;
    el.style.maxHeight = `${css.h}px`;
    el.style.minWidth = "0";
    el.style.minHeight = "0";
    el.style.right = "auto";
    el.style.bottom = "auto";
}

export function applyGameplaySurfaceCssSize(css = gameplayViewportCssSize()): void {
    const vv = window.visualViewport;
    const offsetLeft = vv?.offsetLeft ?? 0;
    const offsetTop = vv?.offsetTop ?? 0;

    for (const id of GAMEPLAY_SURFACE_IDS) {
        const el = document.getElementById(id);
        if (!el) continue;
        el.style.position = "fixed";
        if (id === "touchPlaySteerPad") {
            applyTouchSteerPadCss(el);
            continue;
        }
        el.style.inset = "unset";
        el.style.left = `${offsetLeft}px`;
        el.style.top = `${offsetTop}px`;
        el.style.width = `${css.w}px`;
        el.style.height = `${css.h}px`;
        el.style.maxWidth = `${css.w}px`;
        el.style.maxHeight = `${css.h}px`;
        el.style.minWidth = "0";
        el.style.minHeight = "0";
        el.style.right = "auto";
        el.style.bottom = "auto";
    }
}

/** Map screen-pointer deltas (CSS px) to gameplay canvas coordinates via rendered size. */
export function scalePointerDeltaForGameplay(
    dx: number,
    dy: number,
    surface: HTMLCanvasElement | null = document.getElementById("game") as HTMLCanvasElement | null,
): { dx: number; dy: number } {
    if (dx === 0 && dy === 0) return { dx: 0, dy: 0 };
    if (!surface || surface.width <= 0 || surface.height <= 0) return { dx, dy };
    const rect = surface.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return { dx, dy };
    return {
        dx: dx * (surface.width / rect.width),
        dy: dy * (surface.height / rect.height),
    };
}
